// Platform-context dock section — placeholder for RP-4 of
// docs/planning/firefly_role_context_presentation.md.
//
// Why this section exists at all:
//   Before RP-4 a `platform_owner` / `platform_admin` / `platform_dev` /
//   `platform_trainer` who opened the dock saw nothing actionable —
//   `daily-checklist`, `unsent-leads`, `sent-leads-30d` are all
//   `roles: ["sales"]`-gated, the post-RP-3 `ads-activity` is gated to
//   sales / `ads_guidance_editor` / `is_ads_runner`-cap, and
//   `active-lead-checklist` + `achievements` render mostly empty for
//   platform users who aren't doing rep-shaped work. §4 observation 2 of
//   the planning doc names this the "empty-feeling silent dock" leak (L3).
//
// What this section is NOT:
//   - Not the persistent impersonation indicator. That's RP-5 (L4) and
//     belongs at the dock-chrome level (a peek that cannot be dismissed
//     for the session), not inside the scrollable section list.
//   - Not a cross-org KPI rollup. Real platform telemetry data lands in
//     a later slice — this section is intentionally light so platform
//     users have *something* useful in the dock while the deeper
//     surfaces come online.
//   - Not a render gate for non-platform users. The section's `roleGate`
//     in `sectionRegistry.tsx` already filters it out for org-only roles;
//     this component's internal `auth.platformRole` null-check is a
//     defense-in-depth fallback (mirrors the
//     `useScopedOrganizationId`-null check pattern other sections use).
//
// What it renders today: the resolved platform role, the currently
// scoped org id, and — if `auth.impersonation.active` — the impersonated
// role set. All of these are values the platform user almost always
// wants to confirm at a glance ("which tenant am I in? which role mask
// am I wearing?") and that exist in `useAuth()` today with no extra
// fetch.

import { useAuth } from "@/contexts/AuthContext";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

export type ActivityDockPlatformContextSectionProps = {
  /** WF-0 §6.1 — navigate to Platform home (click-to-nav). */
  onOpenPlatform?: () => void;
};

export function ActivityDockPlatformContextSection({
  onOpenPlatform,
}: ActivityDockPlatformContextSectionProps = {}) {
  const auth = useAuth();
  const scopedOrgId = useScopedOrganizationId();

  // Defense-in-depth: the section's `roleGate` already excludes
  // non-platform users, but if a future regression widened the gate
  // we'd still render an empty / misleading card without this guard.
  if (!auth.platformRole) return null;

  const impersonating = auth.impersonation.active;
  const impersonatedRoles = auth.impersonation.roles.join(", ");

  return (
    <section
      {...{ [FIREFLY_TARGET_ATTR]: "platform-context" }}
      aria-label="Platform context"
      className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-zinc-800/60"
    >
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
        Platform context
      </h3>
      <dl className="mt-1 space-y-0.5 text-[12px] text-gray-700 dark:text-gray-300">
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-gray-500 dark:text-gray-400">Role</dt>
          <dd className="font-medium">{auth.platformRole}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-gray-500 dark:text-gray-400">Org</dt>
          <dd className="truncate font-mono text-[11px]" title={scopedOrgId ?? undefined}>
            {scopedOrgId ?? "—"}
          </dd>
        </div>
        {impersonating ? (
          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-gray-500 dark:text-gray-400">Impersonating</dt>
            <dd className="truncate text-[11px]" title={impersonatedRoles}>
              {impersonatedRoles || "—"}
            </dd>
          </div>
        ) : null}
      </dl>
      {onOpenPlatform ? (
        <button
          type="button"
          onClick={onOpenPlatform}
          className="mt-2 w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:bg-zinc-900 dark:text-gray-100 dark:hover:bg-zinc-800"
        >
          Open Platform home
        </button>
      ) : null}
      <p className="mt-2 text-[11px] italic text-gray-500 dark:text-gray-400">
        Cross-org rollups + bug-triage land in a future slice (RP-5+).
      </p>
    </section>
  );
}
